import styled, { css } from 'styled-components';
import { NavLink } from 'react-router-dom';

import { cFFFFFF } from '#/colors';

import { PORTFOLIO_ROUTE, TRADE_ROUTE } from '#/routes';

import FireIcon from '#/assets/FireIcon';

import { gradientUnderline } from '#/components/shared/styles';
import { verticalSpacerCss } from '#/components/shared/Spacer';
import NotificationsButton from '#/components/common/notifications/NotificationsButton';

import { FeatureFlags } from '#/features/feature-flags';
import { useTranslation } from '#/features/localization/utils';

import MoreMenu from './MoreMenu';
import DevelopersMenu from './DevelopersMenu';
import EnvironmentMenu from './EnvironmentMenu';
import LocaleMenu from './LocaleMenu';
import { navItemStyles } from './styles';

export interface Props {
  readonly className?: string;
}

export default function Nav(props: React.PropsWithChildren<Props>) {
  const { className, children } = props;
  const { t } = useTranslation();

  return (
    <Container className={className}>
      <MainNav id="main-nav" aria-label="Main menu">
        <li css="display: flex; align-items: center; gap: 6px">
          <NavItemLink to={TRADE_ROUTE}>{t('Trade')}</NavItemLink>
          <FireIcon size={15} />
        </li>
        <li>
          <NavItemLink to={PORTFOLIO_ROUTE}>{t('Portfolio')}</NavItemLink>
        </li>
        <li>
          <DevelopersMenu />
        </li>
        <li>
          <MoreMenu />
        </li>
        <div css="flex-grow: 1;" />
      </MainNav>
      <SecondaryNav id="secondary-nav" aria-label="Secondary menu">
        {children}
        {FeatureFlags.getBooleanValue('localization-enabled', false) && (
          <li>
            <LocaleMenu />
          </li>
        )}
        <li>
          <EnvironmentMenu />
        </li>
        <li>
          <NotificationsButton />
        </li>
      </SecondaryNav>
    </Container>
  );
}

const Container = styled.nav`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const MainNav = styled.ul`
  padding: 0;
  margin: 0;
  list-style: none;
  display: flex;
  gap: 40px;
`;

const verticalSpacerBefore = css`
  &:before {
    content: '';
    display: block;
    position: relative;
    left: -15px;
    ${verticalSpacerCss}
  }
`;

const SecondaryNav = styled.ul`
  padding: 0;
  margin: 0;
  list-style: none;
  display: flex;
  gap: 30px;

  li {
    display: flex;
    align-items: center;
    &:not(:first-child) {
      ${verticalSpacerBefore}
    }
  }
`;

export const NavItemLink = styled(NavLink)`
  ${navItemStyles}

  &.active {
    color: ${cFFFFFF};
    ${gradientUnderline}
  }
`;
